import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  PartnerCustomModelSummary,
  PartnerCustomModelTestResult,
  PartnerCustomModelWrite
} from '../../api.js';
import { renderCapabilityBadges, renderModelSpec } from '../model-capability-summary.js';
import { CUSTOM_MODEL_CAPABILITIES, type PartnerCustomModelFormState } from './constants.js';
import {
  pricingDisplayFor,
  pricingPreview,
  testAvailability,
  validatePricingInput,
  type PricingPreview
} from './custom-model-pricing-form.js';
import {
  partnerThinkingLevelOptions,
  partnerThinkingLevelSelection,
  partnerThinkingLevelsSupported
} from './partner-thinking-levels-form.js';

/** A diagnostic result plus whether the form has moved since it was taken. */
export interface CustomModelTestEntry {
  result: PartnerCustomModelTestResult;
  stale: boolean;
}

function describePricing(t: TFunction, preview: PricingPreview): string {
  if (preview.free) return t('partners.customModelPricingFree');
  // The console does not always know the current `usdPerCredit`; name the mode
  // rather than rendering credit figures it cannot compute.
  if (!preview.credits) return t('partners.customModelPricingPaidNoPreview');
  return t('partners.customModelPricingPaid', {
    input: preview.credits.inputPerMillion.toFixed(2),
    output: preview.credits.outputPerMillion.toFixed(2)
  });
}

/** Legacy rows are labelled as such rather than shown as free: the two settle
 *  differently, and conflating them hides which models still need rates. */
function renderPricing(t: TFunction, model: PartnerCustomModelSummary): string {
  const display = pricingDisplayFor(model);
  if (display.kind === 'free') return t('partners.customModelPricingFree');
  if (display.kind === 'legacy') {
    return t('partners.customModelPricingLegacy', {
      billingMode: display.billingMode
    });
  }
  return t('partners.customModelPricingSummary', {
    input: display.inputUsdPerMillion,
    output: display.outputUsdPerMillion,
    cached: display.cachedInputUsdPerMillion ?? display.inputUsdPerMillion,
    cacheWrite: display.cacheWriteInputUsdPerMillion ?? display.inputUsdPerMillion,
    margin: display.marginMultiplier
  });
}

interface PartnerCustomModelsTabProps {
  customModels: PartnerCustomModelSummary[];
  customModelForm: PartnerCustomModelFormState;
  busy: boolean;
  /** Which diagnostic is in flight, if any. Kept separate from `busy` so a test
   *  never blocks the register button and vice versa. */
  testBusy?: 'provider' | 'llm';
  testResults: CustomModelTestEntry[];
  /** `usdPerCredit` from the current rate version, when the console knows it.
   *  The credit preview is explanatory; the server calculation is canonical. */
  usdPerCredit?: number;
  /** Set while an existing model is being edited. The create form and the edit
   *  form are the same fields deliberately — one validator, one set of inputs. */
  editingModelKey?: string;
  ownerLabel?: string;
  kindLabel?: string;
  setCustomModelForm: Dispatch<SetStateAction<PartnerCustomModelFormState>>;
  onRegister: (event: FormEvent) => void;
  onEdit: (modelKey: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: (modelKey: string) => void;
  onTestProvider: () => void;
  onTestLlm: () => void;
  onToggleCapability: (key: keyof PartnerCustomModelWrite['capabilities'], checked: boolean) => void;
}

export function PartnerCustomModelsTab({
  customModels,
  customModelForm,
  busy,
  testBusy,
  testResults,
  usdPerCredit,
  editingModelKey,
  ownerLabel,
  kindLabel,
  setCustomModelForm,
  onRegister,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onTestProvider,
  onTestLlm,
  onToggleCapability
}: PartnerCustomModelsTabProps) {
  const { t } = useTranslation();
  // Validated client-side with the same rules the server applies, so the
  // operator sees a rejection before submitting rather than as a translated 400.
  const editing = editingModelKey !== undefined;
  const pricing = validatePricingInput(customModelForm);
  const thinkingLevelOptions = partnerThinkingLevelOptions(customModelForm.capabilities, customModelForm.litellmModelName || customModelForm.modelKey, customModelForm.thinkingLevels);
  const thinkingLevelSelection = partnerThinkingLevelSelection(
    thinkingLevelOptions,
    customModelForm.thinkingLevels
  );
  const thinkingLevelsSupported = partnerThinkingLevelsSupported(thinkingLevelOptions);
  // A single string rather than a nested ternary chain in JSX: the branches are
  // pricing-invalid, free, paid-with-preview, and paid-without-a-known
  // usdPerCredit, and the last must not render a half preview.
  const pricingMessage = !pricing.ok
    ? t('partners.customModelPricingError.' + pricing.error)
    : describePricing(t, pricingPreview(pricing.pricing, usdPerCredit));
  const testTarget = testAvailability({
    mode: editing ? 'edit' : 'create',
    // Keyed on the API key alone: the base URL is prefilled on an edit, so
    // pairing both fields would read every edit as a rotation. A typed key is
    // the only thing that makes a draft possible.
    //
    // `savedLitellmModelName` is deliberately not supplied. The stored name is
    // in `SECRET_FIELD_NAMES`, so no response can carry it — which means any
    // name typed here is an unsaved change and blocking the test is correct.
    rotateCredential: Boolean(customModelForm.providerApiKey.trim()),
    litellmModelName: customModelForm.litellmModelName,
    providerApiKey: customModelForm.providerApiKey,
    providerBaseUrl: customModelForm.providerBaseUrl
  });
  return (
    <section className="panel">
      <h3>{t('partners.customModels')}</h3>
      <form className="toolbar-form" onSubmit={onRegister}>
        {/* Read-only while editing: modelKey is the URL identifier and a rename
            is a delete plus a fresh create, so an edit here would be silently
            discarded. */}
        <input
          placeholder={t('partners.customModelKey')}
          value={customModelForm.modelKey}
          readOnly={editing}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, modelKey: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelDisplayName')}
          value={customModelForm.displayName}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, displayName: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelDescription')}
          value={customModelForm.description}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, description: event.target.value }))}
          autoComplete="off"
        />
        {/* Also read-only while editing: the reserved id is derived from
            (partnerId, modelKey) and any other value is rejected server-side. */}
        <input
          placeholder={t('partners.customModelBackendId')}
          value={customModelForm.backendId}
          readOnly={editing}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, backendId: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelLiteLLMModel')}
          value={customModelForm.litellmModelName}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, litellmModelName: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelProviderBaseUrl')}
          value={customModelForm.providerBaseUrl}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, providerBaseUrl: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelProviderApiKey')}
          value={customModelForm.providerApiKey}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, providerApiKey: event.target.value }))}
          autoComplete="new-password"
          type="password"
        />
        <input
          placeholder={t('partners.customModelLiteLLMBaseUrl')}
          value={customModelForm.litellmBaseUrl}
          onChange={(event) => setCustomModelForm((current) => ({ ...current, litellmBaseUrl: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelContextWindow')}
          value={customModelForm.contextWindow}
          inputMode="numeric"
          onChange={(event) => setCustomModelForm((current) => ({ ...current, contextWindow: event.target.value }))}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.customModelMaxOutputTokens')}
          value={customModelForm.maxOutputTokens}
          inputMode="numeric"
          onChange={(event) => setCustomModelForm((current) => ({ ...current, maxOutputTokens: event.target.value }))}
          autoComplete="off"
        />
        <label className="field">
          <span>{t('partners.customModelInputRate')}</span>
          <input
            value={customModelForm.inputUsdPerMillion}
            inputMode="decimal"
            min={0}
            step="any"
            onChange={(event) =>
              setCustomModelForm((current) => ({ ...current, inputUsdPerMillion: event.target.value }))
            }
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>{t('partners.customModelOutputRate')}</span>
          <input
            value={customModelForm.outputUsdPerMillion}
            inputMode="decimal"
            min={0}
            step="any"
            onChange={(event) =>
              setCustomModelForm((current) => ({ ...current, outputUsdPerMillion: event.target.value }))
            }
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>{t('partners.customModelCachedInputRate')}</span>
          <input
            value={customModelForm.cachedInputUsdPerMillion}
            inputMode="decimal"
            min={0}
            step="any"
            onChange={(event) => setCustomModelForm((current) => ({ ...current, cachedInputUsdPerMillion: event.target.value }))}
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>{t('partners.customModelCacheWriteInputRate')}</span>
          <input
            value={customModelForm.cacheWriteInputUsdPerMillion}
            inputMode="decimal"
            min={0}
            step="any"
            onChange={(event) => setCustomModelForm((current) => ({ ...current, cacheWriteInputUsdPerMillion: event.target.value }))}
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>{t('partners.customModelMargin')}</span>
          <input
            value={customModelForm.marginMultiplier}
            inputMode="decimal"
            min={0}
            step="any"
            onChange={(event) =>
              setCustomModelForm((current) => ({ ...current, marginMultiplier: event.target.value }))
            }
            autoComplete="off"
          />
        </label>
        <button
          disabled={
            busy ||
            !customModelForm.modelKey.trim() ||
            !customModelForm.displayName.trim() ||
            !customModelForm.litellmModelName.trim() ||
            !pricing.ok ||
            (thinkingLevelsSupported && customModelForm.thinkingLevels !== undefined && thinkingLevelSelection.length === 0) ||
            (!customModelForm.backendId.trim() &&
              (!customModelForm.providerApiKey.trim() || !customModelForm.providerBaseUrl.trim())) ||
            editing
          }
        >
          {t('partners.registerCustomModel')}
        </button>
      </form>
      {editing && (
        <div className="toolbar-form">
          <span className="muted">
            {t('partners.customModelEditing', { modelKey: editingModelKey })}
            {' - '}
            {/* Three fields arrive blank because no response returns them: the API
                key, the LiteLLM model name (a guarded field), and the dedicated
                LiteLLM base URL. Blank keeps the stored value for all three — the
                service preserves the stored LiteLLM ref rather than resetting it. */}
            {t('partners.customModelCredentialKept')}
          </span>
          <button
            type="button"
            disabled={
              busy ||
              !pricing.ok ||
              (thinkingLevelsSupported && customModelForm.thinkingLevels !== undefined && thinkingLevelSelection.length === 0)
            }
            onClick={onSaveEdit}
          >
            {t('common.save')}
          </button>
          <button type="button" disabled={busy} onClick={onCancelEdit}>
            {t('common.cancel')}
          </button>
        </div>
      )}
      {/* Text, not colour alone. The derived mode is the whole point of the
          pricing contract, so the operator must see which one they picked. */}
      <p className="muted" aria-live="polite">
        {pricingMessage}
      </p>
      <p className="muted">{t('partners.customModelCacheRateHelp')}</p>
      <div className="toolbar-form">
        {/* Diagnostics never gate saving: a closed network or a transient
            outage must not block storing a definition. */}
        <button
          type="button"
          disabled={busy || testBusy !== undefined || testTarget.kind === 'unavailable'}
          onClick={() => onTestProvider()}
        >
          {t('partners.customModelTestProvider')}
        </button>
        <button
          type="button"
          disabled={busy || testBusy !== undefined || testTarget.kind === 'unavailable'}
          onClick={() => onTestLlm()}
        >
          {t('partners.customModelTestLlm')}
        </button>
        {testTarget.kind === 'unavailable' && (
          <span className="muted">
            {t('partners.customModelTestUnavailable.' + testTarget.reason)}
          </span>
        )}
      </div>
      {testResults.map((entry) => (
        <p key={entry.result.kind} className="muted" role="status" aria-live="polite">
          {t('partners.customModelTestKind.' + entry.result.kind)}
          {' - '}
          {t('partners.customModelTestStatus.' + entry.result.status)}
          {' - '}
          {t('partners.customModelTestLatency', { ms: entry.result.latencyMs })}
          {' - '}
          {t('partners.customModelTestMessage.' + entry.result.messageCode)}
          {entry.result.responsePreview ? ' - ' + entry.result.responsePreview : ''}
          {/* A stale pass shown as current is the failure that matters: the
              operator fixes a typo, sees the old green, and saves. */}
          {entry.stale ? ' - ' + t('partners.customModelTestStale') : ''}
        </p>
      ))}
      <div className="section-subgroup">
        <h3>{t('partners.customModelCapabilities')}</h3>
        <div className="model-list">
          {CUSTOM_MODEL_CAPABILITIES.map((capability) => (
            <label key={capability} className="check-row">
              <input
                type="checkbox"
                checked={Boolean(customModelForm.capabilities[capability])}
                onChange={(event) => onToggleCapability(capability, event.target.checked)}
              />
              <span>{capability}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="section-subgroup">
        <h3>{t('partners.customModelThinkingLevels')}</h3>
        {thinkingLevelsSupported ? (
          <>
            <div className="model-list">
              {thinkingLevelOptions.map((level) => (
                <label key={level} className="check-row">
                  <input
                    type="checkbox"
                    checked={thinkingLevelSelection.includes(level)}
                    onChange={(event) =>
                      setCustomModelForm((current) => {
                        const options = partnerThinkingLevelOptions(current.capabilities, current.litellmModelName || current.modelKey, current.thinkingLevels);
                        const selection = partnerThinkingLevelSelection(
                          options,
                          current.thinkingLevels
                        );
                        const next = event.target.checked
                          ? options.filter(
                              (option) => option === level || selection.includes(option)
                            )
                          : selection.filter((option) => option !== level);
                        return { ...current, thinkingLevels: next };
                      })
                    }
                  />
                  <span>{t('partners.customModelThinkingLevel.' + level)}</span>
                </label>
              ))}
            </div>
            <p className="muted">{t('partners.customModelThinkingLevelsHelp')}</p>
            {thinkingLevelSelection.length === 0 && (
              <p className="muted" role="alert">
                {t(customModelForm.thinkingLevels === undefined ? 'partners.customModelThinkingLevelsUnset' : 'partners.customModelThinkingLevelsRequired')}
              </p>
            )}
          </>
        ) : (
          <p className="muted">{t('partners.customModelThinkingLevelsOffHelp')}</p>
        )}
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('partners.customModelKey')}</th>
            <th>{t('partners.customModelDisplayName')}</th>
            <th>{t('partners.customModelBilling')}</th>
            <th>{t('partners.status')}</th>
            <th>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {customModels.map((model) => (
            <tr key={model.modelKey}>
              <td>
                custom:{model.modelKey}
                {kindLabel ? <span className="badge">{kindLabel}</span> : null}
                {ownerLabel ? <small>{ownerLabel}</small> : null}
              </td>
              <td>
                <strong>{model.displayName}</strong>
                <small>{renderModelSpec(t, model)}</small>
                {renderCapabilityBadges(t, model)}
              </td>
              <td>{renderPricing(t, model)}</td>
              <td>
                {model.status}
                <small>
                  {t('partners.customModelRevision', { revision: model.revision })}
                  {model.updatedAt ? ' - ' + model.updatedAt : ''}
                </small>
              </td>
              <td>
                {/* A byo-token-budget record cannot be edited: converging its
                    mode would drop the gateway's raw-token cap silently. */}
                <button
                  type="button"
                  disabled={busy || model.billingMode === 'byo-token-budget'}
                  title={
                    model.billingMode === 'byo-token-budget'
                      ? t('partners.customModelEditBlockedByBillingMode')
                      : undefined
                  }
                  onClick={() => onEdit(model.modelKey)}
                >
                  {t('common.edit')}
                </button>
                <button type="button" disabled={busy} onClick={() => onDelete(model.modelKey)}>
                  {t('common.delete')}
                </button>
              </td>
            </tr>
          ))}
          {customModels.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                {t('partners.noCustomModels')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="muted">{t('partners.customModelHint')}</p>
    </section>
  );
}
